import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react'
import ReactDOM from 'react-dom'
import { X, Check, ChevronUp, ChevronDown, Undo2 } from 'lucide-react'

export default function ScorePopover({ score, initialValue, par, anchorRef, onStep, onClear, onUndo, onConfirm, onDismiss, isDeleting }) {
  const popoverRef = useRef(null)
  const [position, setPosition] = useState(null)

  const updatePosition = useCallback(() => {
    if (!anchorRef?.current || !popoverRef.current) return
    const anchor = anchorRef.current.getBoundingClientRect()
    // Guard: if anchor hasn't laid out yet, skip and let the next frame retry
    if (anchor.width === 0 && anchor.height === 0) return
    const popover = popoverRef.current.getBoundingClientRect()
    const viewportW = window.innerWidth

    const gap = 8
    const flip = anchor.top - popover.height - gap < 0
    const top = flip
      ? anchor.bottom + gap
      : anchor.top - popover.height - gap

    let left = anchor.left + anchor.width / 2 - popover.width / 2
    left = Math.max(8, Math.min(left, viewportW - popover.width - 8))

    setPosition({ top, left })
  }, [anchorRef])

  // Position before paint to avoid the flash at (0,0)
  useLayoutEffect(() => {
    updatePosition()
    // If the anchor wasn't ready yet, retry on the next frame
    if (!position) {
      const raf = requestAnimationFrame(updatePosition)
      return () => cancelAnimationFrame(raf)
    }
  }, [updatePosition])

  // Reposition when score changes (chevron taps can shift popover height)
  useEffect(() => {
    updatePosition()
  }, [updatePosition, score])

  // Reposition on scroll and resize (iOS keyboard, content shifts)
  useEffect(() => {
    const scrollParent = anchorRef?.current?.closest('.main-content') || window
    scrollParent.addEventListener('scroll', updatePosition, { passive: true })
    window.addEventListener('resize', updatePosition)
    return () => {
      scrollParent.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
    }
  }, [updatePosition, anchorRef])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) &&
          anchorRef?.current && !anchorRef.current.contains(e.target)) {
        onDismiss()
      }
    }
    document.addEventListener('pointerdown', handleClickOutside)
    return () => document.removeEventListener('pointerdown', handleClickOutside)
  }, [onDismiss, anchorRef])

  const cell = 52

  const btnBase = {
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
  }

  return ReactDOM.createPortal(
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        top: position ? `${position.top}px` : '-9999px',
        left: position ? `${position.left}px` : '-9999px',
        zIndex: 9999,
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)',
        borderRadius: '0.875rem',
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        padding: '0.625rem',
        display: 'grid',
        gridTemplateColumns: `${cell}px ${cell}px ${cell}px`,
        gridTemplateRows: `${cell}px ${cell}px ${cell}px`,
        gap: '0.25rem',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {/* Row 1: undo | up chevron | empty */}
      {initialValue != null && initialValue !== score ? (
        <button
          onClick={(e) => { e.stopPropagation(); onUndo() }}
          style={{
            ...btnBase,
            width: '100%',
            height: '100%',
            borderRadius: '0.75rem',
            border: '1px solid var(--border-color)',
            backgroundColor: 'rgba(59, 130, 246, 0.15)',
            color: '#3b82f6',
            cursor: 'pointer',
          }}
          aria-label="Undo"
        >
          <Undo2 size={18} strokeWidth={2.5} />
        </button>
      ) : (
        <div />
      )}
      <button
        onClick={(e) => { e.stopPropagation(); onStep(1) }}
        disabled={score >= 20}
        style={{
          ...btnBase,
          width: '100%',
          height: '100%',
          borderRadius: '0.75rem',
          border: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-tertiary)',
          color: 'var(--text-primary)',
          cursor: score >= 20 ? 'not-allowed' : 'pointer',
          opacity: score >= 20 ? 0.3 : 1,
        }}
        aria-label="Increase score"
      >
        <ChevronUp size={22} strokeWidth={2.5} />
      </button>
      <div />

      {/* Row 2: X | score | check */}
      <button
        onClick={(e) => { e.stopPropagation(); onClear() }}
        disabled={isDeleting}
        style={{
          ...btnBase,
          width: '100%',
          height: '100%',
          borderRadius: '0.75rem',
          backgroundColor: 'rgba(239, 68, 68, 0.15)',
          color: '#ef4444',
          cursor: isDeleting ? 'not-allowed' : 'pointer',
          opacity: isDeleting ? 0.5 : 1,
        }}
        aria-label="Clear score"
      >
        <X size={22} strokeWidth={2.5} />
      </button>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.75rem',
        fontWeight: '800',
        color: 'var(--text-primary)',
        lineHeight: 1,
      }}>
        {score}
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); (onConfirm || onDismiss)() }}
        style={{
          ...btnBase,
          width: '100%',
          height: '100%',
          borderRadius: '0.75rem',
          backgroundColor: 'rgba(14, 164, 122, 0.15)',
          color: 'var(--accent-primary, #0ea47a)',
          cursor: 'pointer',
        }}
        aria-label="Accept score"
      >
        <Check size={22} strokeWidth={2.5} />
      </button>

      {/* Row 3: down chevron centered */}
      <div />
      <button
        onClick={(e) => { e.stopPropagation(); onStep(-1) }}
        disabled={score <= 1}
        style={{
          ...btnBase,
          width: '100%',
          height: '100%',
          borderRadius: '0.75rem',
          border: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-tertiary)',
          color: 'var(--text-primary)',
          cursor: score <= 1 ? 'not-allowed' : 'pointer',
          opacity: score <= 1 ? 0.3 : 1,
        }}
        aria-label="Decrease score"
      >
        <ChevronDown size={22} strokeWidth={2.5} />
      </button>
      <div />
    </div>,
    document.body
  )
}
